import React, { useCallback, useMemo, useRef } from "react";
import { coerceLabelFontScale, normalizeLabelRotationDeg } from "../../utils/arrowPuzzleGenerator";
import "./ArrowPuzzlePreview.css";

const COLORS = {
  correct: "#2e7d32",
  wrong: "#c62828",
  wordless: "#000000",
  extra: "#000000",
};

const BASE_FONT_FR = 0.42;
const TWO_PI = Math.PI * 2;

function clampScale(s) {
  return coerceLabelFontScale(s);
}

function arrowHeadPoints(cx, cy, dir, size) {
  const s = size;
  switch (dir) {
    case "up":
      return `${cx},${cy - s} ${cx - s * 0.55},${cy + s * 0.35} ${cx + s * 0.55},${cy + s * 0.35}`;
    case "down":
      return `${cx},${cy + s} ${cx - s * 0.55},${cy - s * 0.35} ${cx + s * 0.55},${cy - s * 0.35}`;
    case "left":
      return `${cx - s},${cy} ${cx + s * 0.35},${cy - s * 0.55} ${cx + s * 0.35},${cy + s * 0.55}`;
    case "right":
    default:
      return `${cx + s},${cy} ${cx - s * 0.35},${cy - s * 0.55} ${cx - s * 0.35},${cy + s * 0.55}`;
  }
}

function clientToSvg(svgEl, clientX, clientY) {
  if (!svgEl) return { x: 0, y: 0 };
  const r = svgEl.getBoundingClientRect();
  const vb = svgEl.viewBox?.baseVal;
  if (!vb || vb.width <= 0 || r.width <= 0) return { x: 0, y: 0 };
  const x = ((clientX - r.left) / r.width) * vb.width;
  const y = ((clientY - r.top) / r.height) * vb.height;
  return { x, y };
}

function isWordless(puzzle) {
  return puzzle?.wordless_mode === true || puzzle?.wordless_mode === "true";
}

function labelRowVisible(p, wl, labelEditMode) {
  const labelText = p.label != null ? String(p.label).trim() : "";
  if (!labelText || p.kind === "extra") return false;
  // Admin preview behavior (as requested):
  // - Wordless ON: show labels (and optionally edit).
  // - Wordless OFF: hide labels in the preview.
  // Runtime game uses `wordless_mode` independently.
  if (!wl) return false;
  return true;
}

/**
 * @param {{
 *   puzzle: object | null,
 *   cellSize?: number,
 *   labelEditMode?: boolean,
 *   selectedPathIndex?: number | null,
 *   onSelectPath?: (index: number | null) => void,
 *   onPathLabelLayoutChange?: (pathIndex: number, partial: object) => void,
 * }} props
 */
export default function ArrowPuzzlePreview({
  puzzle,
  cellSize = 11,
  labelEditMode = false,
  selectedPathIndex = null,
  onSelectPath,
  onPathLabelLayoutChange,
}) {
  const svgRef = useRef(null);

  const layout = useMemo(() => {
    const gw = puzzle?.width;
    const gh = puzzle?.height;
    if (!gw || !gh) return null;
    const cs = cellSize;
    const gridW = gw * cs;
    const gridH = gh * cs;
    // Padding from grid geometry only — not label text or labelFontScale — so moving/scaling labels
    // does not shrink or grow the preview frame (the "puzzle" size stays stable).
    const pad = Math.max(cs * 2.75, gridW * 0.2, gridH * 0.2);
    const vw = gridW + pad * 2;
    const vh = gridH + pad * 2;
    const toXY = (r, c) => [pad + c * cs + cs / 2, pad + r * cs + cs / 2];
    return { cs, pad, vw, vh, toXY };
  }, [puzzle?.width, puzzle?.height, cellSize]);

  const attachWindowDrag = useCallback((move, end) => {
    const onMove = (e) => move(e);
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      end?.();
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  }, []);

  const beginMoveLabel = useCallback(
    (e, pathIndex, origDx, origDy, cs) => {
      if (!labelEditMode || !onPathLabelLayoutChange || !svgRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      onSelectPath?.(pathIndex);
      const start = clientToSvg(svgRef.current, e.clientX, e.clientY);
      attachWindowDrag(
        (ev) => {
          if (!svgRef.current) return;
          const cur = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
          onPathLabelLayoutChange(pathIndex, {
            labelDxCells: origDx + (cur.x - start.x) / cs,
            labelDyCells: origDy + (cur.y - start.y) / cs,
          });
        },
        () => {}
      );
    },
    [labelEditMode, onPathLabelLayoutChange, onSelectPath, attachWindowDrag]
  );

  const beginRotate = useCallback(
    (e, pathIndex, cx, cy, origRotDeg) => {
      if (!labelEditMode || !onPathLabelLayoutChange || !svgRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      onSelectPath?.(pathIndex);
      const start = clientToSvg(svgRef.current, e.clientX, e.clientY);
      let lastA = Math.atan2(start.y - cy, start.x - cx);
      let curRot = normalizeLabelRotationDeg(origRotDeg);
      attachWindowDrag(
        (ev) => {
          if (!svgRef.current) return;
          const cur = clientToSvg(svgRef.current, ev.clientX, ev.clientY);
          const a1 = Math.atan2(cur.y - cy, cur.x - cx);
          let da = a1 - lastA;
          if (da > Math.PI) da -= TWO_PI;
          if (da < -Math.PI) da += TWO_PI;
          curRot = normalizeLabelRotationDeg(curRot + (da * 180) / Math.PI);
          lastA = a1;
          onPathLabelLayoutChange(pathIndex, { labelRotationDeg: curRot });
        },
        () => {}
      );
    },
    [labelEditMode, onPathLabelLayoutChange, onSelectPath, attachWindowDrag]
  );

  if (!layout || !puzzle) {
    return (
      <div className="arrow-puzzle-preview arrow-puzzle-preview--empty">
        <p className="themeupdate-muted">Generate a puzzle for this category or load a saved one to see the preview.</p>
      </div>
    );
  }

  const { cs, vw, vh, toXY } = layout;
  const wl = isWordless(puzzle);

  const pathLines = puzzle.paths.map((p, idx) => {
    const cells = p.cells || [];
    if (cells.length < 2) return null;
    const isExtra = p.kind === "extra";
    const stroke = isExtra ? COLORS.extra : p.kind === "correct" ? COLORS.correct : p.kind === "wrong" ? COLORS.wrong : COLORS.wordless;
    const strokeW = isExtra ? Math.max(1, cs * 0.1) : Math.max(1.4, cs * 0.14);
    const d = cells
      .map((cell, i) => {
        const [x, y] = toXY(cell[0], cell[1]);
        return `${i === 0 ? "M" : "L"} ${x.toFixed(2)} ${y.toFixed(2)}`;
      })
      .join(" ");
    const last = cells[cells.length - 1];
    const prev = cells[cells.length - 2];
    const [lx, ly] = toXY(last[0], last[1]);
    const [px, py] = toXY(prev[0], prev[1]);
    const dx = lx - px;
    const dy = ly - py;
    let arrowDir = p.arrow || "right";
    if (Math.abs(dx) + Math.abs(dy) > 0.01) {
      if (Math.abs(dx) > Math.abs(dy)) arrowDir = dx > 0 ? "right" : "left";
      else arrowDir = dy > 0 ? "down" : "up";
    }
    const head = arrowHeadPoints(lx, ly, arrowDir, cs * 0.35);
    const pe = labelEditMode ? "none" : "stroke";
    return (
      <g key={`line-${idx}`}>
        <path
          d={d}
          fill="none"
          stroke={stroke}
          strokeWidth={strokeW}
          strokeLinecap="round"
          strokeLinejoin="round"
          pointerEvents={pe}
        />
        <polygon points={head} fill={stroke} pointerEvents={pe} />
      </g>
    );
  });

  const labelIndices = puzzle.paths
    .map((p, i) => (labelRowVisible(p, wl, labelEditMode) ? i : -1))
    .filter((i) => i >= 0)
    .sort((a, b) => {
      const sa = a === selectedPathIndex ? 1 : 0;
      const sb = b === selectedPathIndex ? 1 : 0;
      return sa - sb;
    });

  const labelEls = labelIndices.map((idx) => {
    const p = puzzle.paths[idx];
    const cells = p.cells || [];
    const labelText = String(p.label || "").trim();
    const mid = cells[Math.floor(cells.length / 2)];
    const [mx, my] = toXY(mid[0], mid[1]);
    const odx = p.labelDxCells != null ? Number(p.labelDxCells) : 0;
    const ody = p.labelDyCells != null ? Number(p.labelDyCells) : 0;
    const tx = mx + odx * cs;
    const ty = my + ody * cs;
    const fontScale = clampScale(p.labelFontScale != null ? p.labelFontScale : 1);
    const fontSize = Math.max(0.35, cs * BASE_FONT_FR * fontScale);
    const rot = normalizeLabelRotationDeg(p.labelRotationDeg != null ? p.labelRotationDeg : 0);
    const halfW = fontSize * 0.52 * Math.max(labelText.length, 3);
    const halfH = fontSize * 0.62;
    const selected = labelEditMode && selectedPathIndex === idx;
    const fill = "#000000";

    const onChromeMove = (e) => beginMoveLabel(e, idx, odx, ody, cs);
    const onRotateStart = (e) => beginRotate(e, idx, tx, ty, rot);

    const rotStem = Math.max(5, fontSize * 0.32);
    const rotBarY = -halfH - rotStem;
    const rotKnobY = -halfH - rotStem - Math.max(3.5, fontSize * 0.28);
    const rotKnobR = Math.max(2.75, Math.min(4, fontSize * 0.22));

    return (
      <g key={`label-${idx}`} transform={`translate(${tx},${ty}) rotate(${rot})`}>
        {selected && (
          <g className="arrow-puzzle-preview__ig-chrome" pointerEvents="all">
            <rect
              className="arrow-puzzle-preview__ig-bbox"
              x={-halfW}
              y={-halfH}
              width={halfW * 2}
              height={halfH * 2}
              rx={1.5}
              vectorEffect="nonScalingStroke"
              onPointerDown={onChromeMove}
            />
            <line
              className="arrow-puzzle-preview__ig-rot-line"
              x1={0}
              y1={-halfH}
              x2={0}
              y2={rotBarY}
              vectorEffect="nonScalingStroke"
            />
            <circle
              className="arrow-puzzle-preview__ig-rot-knob"
              cx={0}
              cy={rotKnobY}
              r={rotKnobR}
              vectorEffect="nonScalingStroke"
              onPointerDown={onRotateStart}
            />
          </g>
        )}
        <text
          x={0}
          y={0}
          textAnchor="middle"
          dominantBaseline="middle"
          className={`arrow-puzzle-preview__label ${labelEditMode ? "arrow-puzzle-preview__label--tap" : ""}`}
          style={{
            fontSize,
            fill,
            pointerEvents: !labelEditMode ? "none" : selected ? "none" : "all",
          }}
          onPointerDown={(e) => {
            if (!labelEditMode) return;
            e.stopPropagation();
            beginMoveLabel(e, idx, odx, ody, cs);
          }}
        >
          {labelText}
        </text>
      </g>
    );
  });

  return (
    <div className={`arrow-puzzle-preview ${labelEditMode ? "arrow-puzzle-preview--edit" : ""}`}>
      <svg
        ref={svgRef}
        className="arrow-puzzle-preview__svg"
        viewBox={`0 0 ${vw} ${vh}`}
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <rect
          x={0}
          y={0}
          width={vw}
          height={vh}
          className="arrow-puzzle-preview__bg"
          pointerEvents={labelEditMode ? "all" : "none"}
          onPointerDown={(e) => {
            if (!labelEditMode || e.target !== e.currentTarget) return;
            onSelectPath?.(null);
          }}
        />
        {pathLines}
        {labelEls}
      </svg>
      {labelEditMode && (
        <p className="arrow-puzzle-preview__edit-hint">
          Tap a label to select. Drag the box to move, top knob to rotate. Set label size with the side panel slider
          (same idea as rotation). Extra lines have no labels.
        </p>
      )}
    </div>
  );
}
